<template>
    <vx-graph
        v-bind="$attrs"
        v-on="$listeners"
        v-model="localValue"
        :type="type"
        class="vx-line-graph"
        ref="chart"
        :config="chartOptions"
        :series="localSeries"
        :full-interface="fullInterface"
        :show-data-labels="showDataLabels && fullInterface"
    >
        <template #tooltip="props" v-if="$scopedSlots.tooltip">
            <slot name="tooltip" v-bind="props" />
        </template>
    </vx-graph>
</template>

<script>
import chartMixins from './chartMixins'

export default {
    name: 'vx-line-graph',
    mixins: [chartMixins],
    props: {
        value: {
            type: null
        },
        stacked: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'area',
            validator: (v) => ['line', 'area'].contains(v)
        },
        enableZoom: {
            type: Boolean,
            default: false
        },
        lineType: {
            type: String,
            default: 'smooth',
            validator: (v) => ['straight', 'stepline', 'smooth'].contains(v)
        },
        lineWidth: {
            type: Number,
            default: 3
        },
        noGrid: {
            type: Boolean,
            default: false
        },
        fullInterface: {
            type: Boolean,
            default: false
        },
        xAnnotations: {
            type: Array,
            default: () => []
        },
        yAnnotations: {
            type: Array,
            default: () => []
        },
        forecastCount: {
            type: Number,
            default: 0
        },
        showDataLabels: {
            type: Boolean,
            default: false
        },
        labelFormat: {
            // only used if the label is a date or a function is passed
            type: [String, Function],
            validator: (v) => {
                if (typeof v === 'string') {
                    return ['day', 'month', 'quarter', 'year', 'fiscalYear'].contains(v)
                }
                return true
            }
        },
        cumulative: {
            type: Boolean,
            default: false
        },
        noSelection: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        localValue: {
            get() {
                if (!this.value) {
                    return null
                }

                var valueId = this.getIdForDataPoint(this.value)
                var newValue = null
                this.localSeries.first((series) => {
                    return series.data.first((dataPoint) => {
                        var dataPointId = this.getIdForDataPoint(dataPoint.original)
                        if (dataPointId == valueId) {
                            newValue = dataPoint
                            return true
                        }
                    })
                })
                return newValue
            },
            set(dataPoint) {
                this.$emit('input', dataPoint && dataPoint.original)
            }
        },
        localSeries() {
            if (this.cumulative) {
                return this.series.map((series) => {
                    var runningTotal = 0
                    var data = series.data.map((dataPoint, i) => {
                        runningTotal += dataPoint.value
                        return {
                            name: this.getLabel(dataPoint),
                            value: runningTotal,
                            original: dataPoint
                        }
                    })

                    return {
                        name: series.name,
                        data
                    }
                })
            }
            return this.series.map((series) => {
                return {
                    ...series,
                    data: series.data.map((dataPoint) => {
                        return {
                            ...dataPoint,
                            name: this.getLabel(dataPoint)
                        }
                    })
                }
            })
        },
        showLegend() {
            return !this.isSingleSeries && this.fullInterface
        },
        chartOptions() {
            return {
                annotations: {
                    xaxis: this.xAnnotations || [],
                    yaxis: this.yAnnotations || []
                },
                yaxis: this.yAxis,
                xaxis: this.xAxis,
                chart: {
                    stacked: this.stacked,
                    sparkline: {
                        enabled: !this.fullInterface
                    },
                    zoom: {
                        type: 'x',
                        autoScaleYAxis: true,
                        enabled: this.enableZoom && this.fullInterface
                    }
                },
                stroke: {
                    show: true,
                    curve: this.lineType,
                    width: this.lineWidth
                },
                fill: {
                    gradient: {
                        enabled: true,
                        opacityFrom: 0.6,
                        opacityTo: 0
                    }
                },
                grid: {
                    show: this.fullInterface && !this.noGrid
                },
                markers: {
                    size: this.markerSize,
                    strokeOpacity: 0,
                    fillOpacity: this.localValue ? 0.2 : 0.5,
                    shape: 'circle',
                    radius: 0,
                    strokeWidth: 0,
                    hover: {
                        size: this.markerSize + 3
                    }
                },
                tooltip: {
                    intersect: true,
                    shared: false
                    // only works if the tooltip is shared which prevents selection
                    // fixed: {
                    //     enabled: true,
                    //     position: 'topRight',
                    //     offsetX: -40,
                    //     offsetY: 25
                    // }
                },
                legend: {
                    show: this.showLegend,
                    position: 'bottom'
                },
                forecastDataPoints: {
                    count: this.forecastCount,
                    strokeWidth: this.strokeWidth,
                    dashArray: 10
                }
            }
        },
        markerSize() {
            if (this.noSelection) {
                return 0
            }
            if (this.fullInterface) {
                return this.lineWidth * 2
            }
            return this.lineWidth + 3
        },
        xAxis() {
            return this.horizontal ? this.yAxis_x : this.xAxis_x
        },
        xAxis_x() {
            var isShown = this.fullInterface
            return {
                show: isShown,
                labels: {
                    show: isShown,
                    formatter: this.formatValue
                },
                axisTicks: {
                    show: isShown
                },
                axisBorder: {
                    show: isShown
                }
            }
        },
        yAxis() {
            return this.horizontal ? this.xAxis_x : this.yAxis_x
        },
        yAxis_x() {
            var isShown = this.fullInterface && !this.showDataLabels
            return {
                show: isShown,
                min: this.minValue,
                labels: {
                    show: isShown,
                    formatter: this.formatValue
                },
                axisTicks: {
                    show: isShown
                },
                axisBorder: {
                    show: isShown
                }
            }
        }
    },
    methods: {
        getLabel(dataPoint) {
            if (!this.labelFormat) {
                return dataPoint.name
            }
            if (typeof this.labelFormat === 'function') {
                return this.labelFormat(dataPoint)
            }
            if (dataPoint.name instanceof Date) {
                return dataPoint.name.getPeriodLabel()
            }

            if (Date.isValidDateString(dataPoint.name)) {
                return new Date(dataPoint.name).getPeriodLabel()
            }
            throw 'DataPoint name could not be correctly translated to a valid label'
        }
    }
}
</script>

<style lang="scss" scoped>
.theme-dark {
    .vx-line-graph {
        ::v-deep .apexcharts-marker {
            &:hover,
            &[selected='true'] {
                stroke: white;
            }
        }
    }
}
.vx-line-graph {
    ::v-deep .apexcharts-marker {
        &:hover,
        &[selected='true'] {
            fill-opacity: 1;
            stroke-width: 3;
            stroke: black;
            filter: none;
        }
    }
}
</style>
